import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  StickyNote, X, Plus, Trash2, Share2, Loader2, Save, Edit3,
  MessageSquare, Send, ChevronLeft, User,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("en-GB", { hour12: false }); } catch { return iso; } };

/**
 * Floating "notes" chip for the admin header. Notes are shared between
 * ALL admins (backend enforces admin-only). Every note supports:
 *  • Click-to-open thread view
 *  • Inline edit of title/body
 *  • Threaded replies (like a mini team chat)
 *  • Sort: last activity first (last reply timestamp OR created_at)
 *  • WhatsApp export forwards title + body + last replies
 */
const AdminNotes = () => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  // Thread view for a single note
  const [selectedId, setSelectedId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [replying, setReplying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/notes`);
      setNotes(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const totalReplies = useMemo(
    () => notes.reduce((s, n) => s + ((n.replies || []).length), 0),
    [notes],
  );

  const selected = useMemo(
    () => notes.find((n) => n.id === selectedId) || null,
    [notes, selectedId],
  );

  const submit = async () => {
    const t = title.trim();
    const b = body.trim();
    if (!t && !b) { toast.error("اكتب عنوان أو نص الملاحظة"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/notes`, { title: t, body: b });
      toast.success("تم الحفظ");
      setTitle(""); setBody(""); setAdding(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل الحفظ"); }
    finally { setSaving(false); }
  };

  const openThread = (n) => {
    setSelectedId(n.id);
    setEditing(false);
    setEditTitle(n.title || "");
    setEditBody(n.body || "");
    setReplyBody("");
  };

  const saveEdit = async () => {
    const t = editTitle.trim();
    const b = editBody.trim();
    if (!t && !b) { toast.error("اكتب عنوان أو نص للملاحظة"); return; }
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/notes/${selectedId}`, { title: t, body: b });
      toast.success("تم التعديل");
      setNotes((arr) => arr.map((x) => x.id === selectedId ? data.note : x));
      setEditing(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل التعديل"); }
    finally { setSaving(false); }
  };

  const sendReply = async () => {
    const b = replyBody.trim();
    if (!b) { toast.error("اكتب نص الرد"); return; }
    setReplying(true);
    try {
      const { data } = await axios.post(`${API}/admin/notes/${selectedId}/reply`, { body: b });
      setNotes((arr) => arr.map((x) => x.id === selectedId ? data.note : x));
      setReplyBody("");
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل الإرسال"); }
    finally { setReplying(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("حذف هذه الملاحظة؟")) return;
    try {
      await axios.delete(`${API}/admin/notes/${id}`);
      setNotes((n) => n.filter((x) => x.id !== id));
      if (selectedId === id) setSelectedId(null);
      toast.success("تم الحذف");
    } catch { toast.error("فشل الحذف"); }
  };

  const shareWa = (note) => {
    const lines = [`📝 ${note.title}`];
    if (note.body) lines.push(note.body);
    const replies = note.replies || [];
    if (replies.length) {
      lines.push("");
      lines.push("💬 الردود:");
      replies.forEach((r, i) => {
        lines.push(`${i + 1}. ${r.created_by_name || ""}: ${r.body}`);
      });
    }
    lines.push("");
    lines.push(`— ${fmtDT(note.created_at)}`);
    window.open(`https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
  };

  // ── UI helpers ───────────────────────────────────────────────────────
  const renderThread = () => {
    if (!selected) return null;
    const replies = selected.replies || [];
    return (
      <>
        {/* Note body block */}
        <div className="p-3 bg-gradient-to-l from-amber-50 to-orange-50 border-b border-amber-100">
          {editing ? (
            <div className="space-y-2">
              <input
                value={editTitle} onChange={(e) => setEditTitle(e.target.value)}
                data-testid="admin-notes-edit-title"
                placeholder="عنوان الملاحظة"
                className="w-full px-3 py-2.5 rounded-xl border-2 border-orange-200 focus:border-orange-500 bg-white text-sm font-bold focus:outline-none text-slate-900"
              />
              <textarea
                value={editBody} onChange={(e) => setEditBody(e.target.value)}
                data-testid="admin-notes-edit-body"
                rows={3}
                placeholder="نص الملاحظة"
                className="w-full px-3 py-2 rounded-xl border-2 border-orange-200 focus:border-orange-500 bg-white text-sm focus:outline-none resize-y min-h-[80px] text-slate-900"
              />
              <div className="flex gap-2">
                <button
                  onClick={saveEdit} disabled={saving}
                  data-testid="admin-notes-edit-save"
                  className="flex-1 py-2.5 rounded-xl text-white font-black text-sm flex items-center justify-center gap-1.5 shadow-md disabled:opacity-40"
                  style={{ background: "linear-gradient(135deg, #F5A623, #E85D45)" }}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  حفظ التعديل
                </button>
                <button
                  onClick={() => { setEditing(false); setEditTitle(selected.title); setEditBody(selected.body || ""); }}
                  className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm"
                >
                  إلغاء
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-black text-base text-slate-800 break-words">{selected.title}</p>
                  {selected.body && (
                    <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap break-words leading-relaxed">
                      {selected.body}
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-500">
                    <span>🕐 {fmtDT(selected.created_at)}</span>
                    {selected.created_by_name && <span>✍️ {selected.created_by_name}</span>}
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    onClick={() => setEditing(true)}
                    data-testid={`admin-note-edit-${selected.id}`}
                    title="تعديل"
                    className="w-8 h-8 rounded-lg bg-orange-50 hover:bg-orange-100 flex items-center justify-center"
                  >
                    <Edit3 className="w-4 h-4 text-orange-600" />
                  </button>
                  <button
                    onClick={() => shareWa(selected)}
                    data-testid={`admin-note-wa-${selected.id}`}
                    title="واتساب"
                    className="w-8 h-8 rounded-lg bg-green-50 hover:bg-green-100 flex items-center justify-center"
                  >
                    <Share2 className="w-4 h-4 text-green-600" />
                  </button>
                  <button
                    onClick={() => remove(selected.id)}
                    data-testid={`admin-note-del-${selected.id}`}
                    title="حذف"
                    className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Replies stream */}
        <div className="flex-1 overflow-y-auto p-3 bg-slate-50 space-y-2" data-testid="admin-note-replies">
          {replies.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">
              <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="font-bold text-slate-500">لا توجد ردود</p>
              <p className="text-xs">اكتب أول رد بالأسفل</p>
            </div>
          ) : (
            replies.map((r) => (
              <div key={r.id}
                data-testid={`admin-note-reply-${r.id}`}
                className="bg-white rounded-2xl p-3 shadow-sm border border-slate-200">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white shrink-0"
                    style={{ background: "linear-gradient(135deg, #F5A623, #E85D45)" }}>
                    <User className="w-4 h-4" />
                  </div>
                  <p className="font-black text-sm text-slate-800">{r.created_by_name || "أدمن"}</p>
                  <span className="text-[10px] text-slate-400 mr-auto">{fmtDT(r.created_at)}</span>
                </div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap break-words leading-relaxed pr-9">
                  {r.body}
                </p>
              </div>
            ))
          )}
        </div>

        {/* Reply composer */}
        <div className="p-3 bg-white border-t border-slate-100 flex items-center gap-2">
          <input
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
            data-testid="admin-notes-reply-input"
            placeholder="اكتب رد..."
            className="flex-1 px-3 py-2.5 rounded-xl border-2 border-orange-200 focus:border-orange-500 bg-slate-50 text-sm focus:outline-none text-slate-900"
          />
          <button
            onClick={sendReply}
            disabled={replying || !replyBody.trim()}
            data-testid="admin-notes-reply-send"
            className="w-11 h-11 rounded-xl text-white flex items-center justify-center shadow-md disabled:opacity-40 shrink-0"
            style={{ background: "linear-gradient(135deg, #F5A623, #E85D45)" }}
          >
            {replying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </>
    );
  };

  return (
    <>
      {/* Chip button — sits inline next to bell/coupons */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="admin-notes-btn"
        title="الملاحظات"
        className="relative w-10 h-10 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-all border-2 border-white"
        style={{ background: "linear-gradient(135deg, #F5A623, #E85D45)" }}
      >
        <StickyNote className="w-5 h-5 text-white" strokeWidth={2.5} />
        {(notes.length + totalReplies) > 0 && (
          <span className="absolute -top-1 -left-1 min-w-[18px] h-[18px] rounded-full bg-white border-2 border-orange-500 text-[10px] font-black text-orange-600 flex items-center justify-center px-1">
            {notes.length}
          </span>
        )}
      </button>

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-[140] bg-black/60 flex items-center justify-center p-3"
          dir="rtl"
          data-testid="admin-notes-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col">
            {/* Header */}
            <div className="p-4 text-white relative overflow-hidden"
              style={{ background: "linear-gradient(135deg, #F5A623, #E85D45)" }}>
              <div className="absolute -top-6 -left-6 w-28 h-28 rounded-full bg-white/10" />
              <div className="absolute -bottom-8 -right-4 w-24 h-24 rounded-full bg-white/5" />
              <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {selected && (
                    <button
                      onClick={() => setSelectedId(null)}
                      data-testid="admin-notes-back"
                      className="w-10 h-10 rounded-full bg-white/25 hover:bg-white/40 backdrop-blur flex items-center justify-center">
                      <ChevronLeft className="w-5 h-5 rotate-180" />
                    </button>
                  )}
                  <div className="w-12 h-12 rounded-2xl bg-white/25 backdrop-blur flex items-center justify-center border-2 border-white/40">
                    <StickyNote className="w-6 h-6" strokeWidth={2.5} />
                  </div>
                  <div>
                    <p className="text-xs opacity-90">
                      {selected ? "محادثة الملاحظة" : "الأمور اللي ما تريد تنساها"}
                    </p>
                    <h3 className="font-black text-xl">
                      {selected ? "📝 المحادثة" : `📝 الملاحظات (${notes.length})`}
                    </h3>
                  </div>
                </div>
                <button onClick={() => { setOpen(false); setSelectedId(null); }} data-testid="admin-notes-close"
                  className="w-10 h-10 rounded-full bg-white/25 hover:bg-white/40 backdrop-blur flex items-center justify-center">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {selected ? renderThread() : (
              <>
                {/* Add form */}
                <div className="p-3 bg-gradient-to-l from-amber-50 to-orange-50 border-b border-amber-100">
                  {!adding ? (
                    <button onClick={() => setAdding(true)} data-testid="admin-notes-add-btn"
                      className="w-full py-3 rounded-2xl border-2 border-dashed border-orange-300 text-orange-700 font-bold text-sm hover:bg-orange-50 flex items-center justify-center gap-2 transition-all">
                      <Plus className="w-5 h-5" /> إضافة ملاحظة جديدة
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <input
                        value={title} onChange={(e) => setTitle(e.target.value)}
                        data-testid="admin-notes-title"
                        placeholder="عنوان الملاحظة (اختياري)"
                        className="w-full px-3 py-2.5 rounded-xl border-2 border-orange-200 focus:border-orange-500 bg-white text-sm font-bold focus:outline-none text-slate-900 placeholder-slate-400"
                        autoFocus
                      />
                      <textarea
                        value={body} onChange={(e) => setBody(e.target.value)}
                        data-testid="admin-notes-body"
                        placeholder="اكتب نص الملاحظة هنا..."
                        rows={3}
                        className="w-full px-3 py-2 rounded-xl border-2 border-orange-200 focus:border-orange-500 bg-white text-sm focus:outline-none resize-y min-h-[80px] text-slate-900 placeholder-slate-400"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={submit} disabled={saving}
                          data-testid="admin-notes-save"
                          className="flex-1 py-2.5 rounded-xl text-white font-black text-sm flex items-center justify-center gap-1.5 shadow-md disabled:opacity-40"
                          style={{ background: "linear-gradient(135deg, #F5A623, #E85D45)" }}
                        >
                          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          حفظ
                        </button>
                        <button
                          onClick={() => { setAdding(false); setTitle(""); setBody(""); }}
                          className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm"
                        >
                          إلغاء
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Notes list — click a card to open thread */}
                <div className="flex-1 overflow-y-auto p-3 bg-slate-50">
                  {loading ? (
                    <div className="text-center py-10">
                      <Loader2 className="w-8 h-8 animate-spin inline text-orange-400" />
                    </div>
                  ) : notes.length === 0 ? (
                    <div className="text-center py-12 text-slate-400 text-sm">
                      <StickyNote className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p className="font-bold text-slate-500">لا توجد ملاحظات بعد</p>
                      <p className="text-xs">اضغط "إضافة ملاحظة" لأول ملاحظة</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {notes.map((n, i) => {
                        const rcount = (n.replies || []).length;
                        return (
                          <button
                            key={n.id}
                            type="button"
                            onClick={() => openThread(n)}
                            data-testid={`admin-note-${n.id}`}
                            className="w-full text-right bg-white rounded-2xl p-3 shadow-sm border-2 border-amber-100 hover:border-amber-300 hover:shadow-md transition-all"
                          >
                            <div className="flex items-start gap-2">
                              <div className="w-8 h-8 rounded-full flex items-center justify-center font-black text-xs shrink-0 text-white shadow-md"
                                style={{ background: "linear-gradient(135deg, #F5A623, #E85D45)" }}>
                                {i + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-black text-sm text-slate-800 break-words">
                                  {n.title}
                                </p>
                                {n.body && (
                                  <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap break-words leading-relaxed line-clamp-2">
                                    {n.body}
                                  </p>
                                )}
                                <div className="flex items-center gap-2 mt-1.5 text-[10px] text-slate-500">
                                  <span>🕐 {fmtDT(n.created_at)}</span>
                                  {n.created_by_name && <span>✍️ {n.created_by_name}</span>}
                                </div>
                              </div>
                              {rcount > 0 && (
                                <div className="flex flex-col items-center shrink-0 bg-orange-50 border-2 border-orange-200 rounded-xl px-2 py-1">
                                  <MessageSquare className="w-4 h-4 text-orange-600" />
                                  <span className="text-[11px] font-black text-orange-700 mt-0.5">{rcount}</span>
                                </div>
                              )}
                              <ChevronLeft className="w-5 h-5 text-slate-300 shrink-0 mt-2" />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AdminNotes;
